import { MutableRefObject } from 'react';
import GameEngine from 'react-game-engine';
import { Engine } from 'matter-js';
import type YouTubePlayer from 'youtube-player';
import { NavigateFunction } from 'react-router-dom';
import { useAnimation } from 'framer-motion';

import COLORS from '~/constants/colors';
import { clone } from '~/utils';

import { GAME_MODES, INPUT_MODES } from '../../../../constants/defaults';
import uris from '../../../../uris';
import { Dim, Entities, SystemExtras } from './models';
import { Settings, Session } from '../../../../models';
import { DEFAULT_STATE } from './constants/defaults';
import { POINTS } from './constants/mechanics';
import youTube from './engine/systems/youTube';
import playTempo from './engine/systems/play/tempo';
import playHighway from './engine/systems/play/highway';
import playInput from './engine/systems/play/input';
import recordTempo from './engine/systems/record/tempo';
import recordHighway from './engine/systems/record/highway';
import recordInput from './engine/systems/record/input';
import reviewTempo from './engine/systems/review/tempo';
import reviewHighway from './engine/systems/review/highway';
import reviewInput from './engine/systems/review/input';
import inputPad from './engine/entities/inputPad';
import recordPad from './engine/entities/recordPad';
import feedback from './engine/entities/feedback';
import stopwatch from './engine/entities/stopwatch';
import metrics from './engine/entities/metrics';
import receptor from './engine/entities/receptor';
import exit from './engine/entities/exit';

export default (
  windowDims: Dim,
  animates: ReturnType<typeof useAnimation>[],
  engine: Engine,
  videoPlayer: YouTubePlayer,
  navigate: NavigateFunction,
  settings: Settings,
  session: Session,
  gameEngineRef: MutableRefObject<GameEngine>
): ((
  gameMode: string
) => [
  ((entities: Entities, extras: SystemExtras) => Entities)[],
  () => Partial<Entities>
]) => {
  const receptorAnimates = {
    0: animates[0],
    1: animates[1],
    2: animates[2],
  };
  const getPlayEntities = () => ({
    receptor: receptor(windowDims, receptorAnimates, settings.inputMode),
    bridges: [],
    metrics: metrics(
      windowDims,
      (settings.rhythm?.timings.length ?? 0) * POINTS.perfect
    ),
    exit: exit(windowDims, uris.ending, gameEngineRef),
    correctFeedback: feedback(
      windowDims,
      settings.inputMode,
      'CORRECT',
      animates[3],
      COLORS.pastelGreen
    ),
    perfectFeedback: feedback(
      windowDims,
      settings.inputMode,
      'PERFECT',
      animates[4]
    ),
    inputPad: inputPad(windowDims, settings, gameEngineRef),
  });
  const getRecordEntities = () => ({
    receptor: receptor(windowDims, receptorAnimates),
    stopwatch: stopwatch(windowDims, INPUT_MODES.jog, session.videoStartTime),
    recordPad: recordPad(windowDims, gameEngineRef),
    exit: exit(windowDims, uris.production, gameEngineRef),
  });
  const getReviewEntities = () => ({
    receptor: receptor(
      windowDims,
      receptorAnimates,
      INPUT_MODES.jog,
      INPUT_MODES.key
    ),
    stopwatch: stopwatch(windowDims, INPUT_MODES.key, session.videoStartTime),
    exit: exit(windowDims, uris.studio, gameEngineRef),
  });

  return (gameMode) => [
    {
      [GAME_MODES.play]: [youTube, playTempo, playHighway, playInput],
      [GAME_MODES.record]: [youTube, recordTempo, recordHighway, recordInput],
      [GAME_MODES.review]: [youTube, reviewTempo, reviewHighway, reviewInput],
    }[gameMode],
    () => ({
      physics: { engine, windowDims },
      bars: [],
      notes: [],
      videoPlayer,
      navigate,
      settings,
      session,
      state: clone(DEFAULT_STATE), // Needed to get fresh `DEFAULT_STATE` each call
      ...(gameMode === GAME_MODES.play && getPlayEntities()),
      ...(gameMode === GAME_MODES.record && getRecordEntities()),
      ...(gameMode === GAME_MODES.review && getReviewEntities()),
    }),
  ];
};
